import * as React from "react";
import {
  forwardRef,
  useCallback,
  useEffect,
  useId,
  useImperativeHandle,
  useRef,
} from "react";

import {
  Cycle,
  KnotOptions,
  KnotState,
  S,
  cycleLength,
  knotGeometry,
  prefersReducedMotion,
  stateAt,
  tieCycle,
  tugCycle,
  untieCycle,
} from "../../lib/knot";

type SvgProps = {
  className?: string;
  /** In knot units; the default frames a short cord with the knot on it. */
  viewBox?: string;
  /** Accessible name. Without one the knot is decorative. */
  title?: string;
};

// The mask covers far more than any viewBox we draw into.
export const MASK = { x: -200, y: -200, width: 600, height: 600 };

const dash = (f: number) => (f < 0.999 ? `${f} 2` : undefined);
const hidden = (f: number) => (f > 0.001 ? undefined : { display: "none" });

const svgAttrs = (
  { className, viewBox = "8 18 48 28", title }: SvgProps,
  kind: string,
) => ({
  className: ["knot", kind, className].filter(Boolean).join(" "),
  viewBox,
  role: title ? "img" : undefined,
  "aria-label": title,
  "aria-hidden": title ? undefined : true,
  focusable: "false" as const,
});

// Where a turn crosses the cord the cord is cut away around it, so the gap
// shows whatever lies behind the knot: a card, the glass header, either theme.
const Drawn: React.FC<{
  maskId: string;
  options: KnotOptions;
  cord: boolean;
  casing: boolean;
}> = ({ maskId, options, cord, casing }) => {
  const g = knotGeometry(options);
  return (
    <>
      {cord && (
        <>
          <mask id={maskId} maskUnits="userSpaceOnUse" {...MASK}>
            <rect {...MASK} fill="#fff" />
            {g.turns.map((t, i) => (
              <path
                key={i}
                className="knot-mask"
                d={t.d}
                stroke="#000"
                strokeWidth={g.cw}
                pathLength={1}
                strokeDasharray={dash(t.f)}
                style={hidden(t.f)}
              />
            ))}
          </mask>
          <path
            className="knot-cord"
            d={g.cord}
            strokeWidth={g.w}
            mask={`url(#${maskId})`}
          />
        </>
      )}
      {g.turns.map((t, i) => (
        <g key={i}>
          {casing && (
            <path
              className="knot-case"
              d={t.d}
              strokeWidth={g.cw}
              pathLength={1}
              strokeDasharray={dash(t.f)}
              style={hidden(t.f)}
            />
          )}
          <path
            className="knot-turn"
            d={t.d}
            strokeWidth={g.tw}
            pathLength={1}
            strokeDasharray={dash(t.f)}
            style={hidden(t.f)}
          />
        </g>
      ))}
    </>
  );
};

export const useMaskId = () => `knot${useId().replace(/:/g, "")}`;

/**
 * The long knot at rest: `p` turns wound on, `c` tight (see src/lib/knot.ts).
 * `cord={false}` draws just the turns, for a knot tied on a cord drawn some
 * other way; `casing` then paints the gap in `--knot-casing`.
 */
export const Knot: React.FC<
  SvgProps & KnotOptions & { cord?: boolean; casing?: boolean }
> = ({
  className,
  viewBox,
  title,
  cord = true,
  casing = false,
  ...options
}) => {
  const maskId = useMaskId();
  return (
    <svg {...svgAttrs({ className, viewBox, title }, "knot-still")}>
      <Drawn maskId={maskId} options={options} cord={cord} casing={casing} />
    </svg>
  );
};

export type KnotMotion = {
  tie: () => void;
  untie: () => void;
  tug: () => void;
};

type AnimatedProps = SvgProps & {
  n?: number;
  x0?: number;
  x1?: number;
  /** Start as a bare cord and tie the knot once mounted. */
  tieOnMount?: boolean;
};

/**
 * A knot that ties, unties and takes a tug. Frames are painted straight into
 * the SVG rather than through React state, so an animation never re-renders
 * its parent.
 */
export const AnimatedKnot = forwardRef<KnotMotion, AnimatedProps>(
  ({ className, viewBox, title, n = 3, x0, x1, tieOnMount = false }, ref) => {
    const maskId = useMaskId();
    const svg = useRef<SVGSVGElement>(null);
    const raf = useRef(0);
    const first = useRef<KnotState>(
      tieOnMount && !prefersReducedMotion() ? S(0, 0) : S(n, 1),
    );
    const state = useRef<KnotState>(first.current);

    const paint = useCallback(
      (st: KnotState) => {
        state.current = st;
        const el = svg.current;
        if (!el) return;
        const g = knotGeometry({ n, x0, x1, ...st });
        const masks = el.querySelectorAll<SVGPathElement>(".knot-mask");
        const turns = el.querySelectorAll<SVGPathElement>(".knot-turn");
        g.turns.forEach((t, i) => {
          for (const path of [masks[i], turns[i]]) {
            path.setAttribute("d", t.d);
            const d = dash(t.f);
            if (d) path.setAttribute("stroke-dasharray", d);
            else path.removeAttribute("stroke-dasharray");
            path.style.display = t.f > 0.001 ? "" : "none";
          }
        });
      },
      [n, x0, x1],
    );

    const run = useCallback(
      (cycle: Cycle) => {
        cancelAnimationFrame(raf.current);
        const end = cycle[cycle.length - 1][2];
        if (prefersReducedMotion()) {
          paint(end);
          return;
        }
        const total = cycleLength(cycle);
        let start: number | null = null;
        const tick = (now: number) => {
          start ??= now;
          const t = now - start;
          if (t >= total) {
            paint(end);
            raf.current = 0;
            return;
          }
          paint(stateAt(t, cycle));
          raf.current = requestAnimationFrame(tick);
        };
        raf.current = requestAnimationFrame(tick);
      },
      [paint],
    );

    useImperativeHandle(
      ref,
      () => ({
        tie: () => run(tieCycle(n, state.current)),
        untie: () => run(untieCycle(n, state.current)),
        tug: () => run(tugCycle(n, state.current)),
      }),
      [n, run],
    );

    useEffect(() => {
      if (tieOnMount) run(tieCycle(n));
      return () => cancelAnimationFrame(raf.current);
    }, [tieOnMount, run, n]);

    return (
      <svg ref={svg} {...svgAttrs({ className, viewBox, title }, "knot-live")}>
        <Drawn
          maskId={maskId}
          options={{ n, x0, x1, ...first.current }}
          cord
          casing={false}
        />
      </svg>
    );
  },
);
AnimatedKnot.displayName = "AnimatedKnot";
